import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import GroupIcon from '@mui/icons-material/Group';
import CircularProgress from '@mui/material/CircularProgress';
import { userApi, ApiError } from '../../../services/api';
import styles from './ProfileInviteFriendsScreen.module.css';

interface InviteStats {
  totalInvites: number;
  acceptedInvites: number;
  pendingInvites: number;
  rewardsEarned: number;
}

interface InviteHistory {
  id: string;
  recipientEmail?: string;
  recipientPhone?: string;
  status: 'pending' | 'accepted' | 'expired';
  sentAt: string;
  acceptedAt?: string;
  rewardAmount?: number;
}

const ProfileInviteFriendsScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [inviteMethod, setInviteMethod] = useState<'email' | 'sms'>('email');
  const [recipient, setRecipient] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [stats, setStats] = useState<InviteStats | null>(null);
  const [inviteHistory, setInviteHistory] = useState<InviteHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Mock referral code - in production, this would come from the user's profile
  const referralCode = 'MARIO2025';
  const referralLink = `https://mariohealth.com/join?ref=${referralCode}`;

  useEffect(() => {
    fetchInviteData();
  }, []);

  const fetchInviteData = async () => {
    try {
      const result = await userApi.getInviteStats();
      
      setStats(result.stats);
      setInviteHistory(result.history);
    } catch (error) {
      console.error('Error fetching invite data:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^\+?[\d\s-()]+$/;
    return phone.length >= 10 && phoneRegex.test(phone);
  };

  const handleInvite = async () => {
    setInviteError('');
    setInviteSuccess(false);
    
    // Validate input
    if (inviteMethod === 'email' && !validateEmail(recipient)) {
      setInviteError('Please enter a valid email address');
      return;
    }
    
    if (inviteMethod === 'sms' && !validatePhone(recipient)) {
      setInviteError('Please enter a valid phone number');
      return;
    }
    
    setIsInviting(true);
    
    try {
      await userApi.sendInvite({
        method: inviteMethod,
        recipient,
        referralCode
      });
      
      setInviteSuccess(true);
      setRecipient('');
      await fetchInviteData(); // Refresh stats and history
      setTimeout(() => setInviteSuccess(false), 5000);
    } catch (error) {
      if (error instanceof ApiError) {
        setInviteError(error.message);
      } else {
        setInviteError('Failed to send invitation. Please try again.');
      }
    } finally {
      setIsInviting(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Mario Health',
          text: 'Save on healthcare costs with Mario Health! Use my referral code for a special bonus.',
          url: referralLink
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      handleCopyLink();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'accepted':
        return styles.statusAccepted;
      case 'pending':
        return styles.statusPending;
      case 'expired':
        return styles.statusExpired;
      default:
        return '';
    }
  };

  if (isLoading) {
    return (
      <div className={styles.profileInviteFriends}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileInviteFriends}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/profile')}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Invite Friends</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.content}>
        {/* Rewards Banner */}
        <div className={styles.rewardsBanner}>
          <CardGiftcardIcon className={styles.rewardIcon} />
          <div>
            <h2 className={styles.rewardTitle}>Earn $20 for Each Friend</h2>
            <p className={styles.rewardText}>
              When your friends join and complete their first appointment, you both get $20 in credits!
            </p>
          </div>
        </div>

        {/* Stats Section */}
        {stats && (
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <PersonAddIcon className={styles.statIcon} />
              <div className={styles.statValue}>{stats.totalInvites}</div>
              <div className={styles.statLabel}>Invites Sent</div>
            </div>
            <div className={styles.statCard}>
              <GroupIcon className={styles.statIcon} />
              <div className={styles.statValue}>{stats.acceptedInvites}</div>
              <div className={styles.statLabel}>Friends Joined</div>
            </div>
            <div className={styles.statCard}>
              <CardGiftcardIcon className={styles.statIcon} />
              <div className={styles.statValue}>${stats.rewardsEarned}</div>
              <div className={styles.statLabel}>Rewards Earned</div>
            </div>
          </div>
        )}

        {/* Referral Link Section */}
        <div className={styles.referralSection}>
          <h3 className={styles.sectionTitle}>Your Referral Link</h3>
          <div className={styles.referralBox}>
            <div className={styles.referralContent}>
              <p className={styles.referralLabel}>Share this link with friends:</p>
              <p className={styles.referralLink}>{referralLink}</p>
              <p className={styles.referralCode}>Referral Code: <strong>{referralCode}</strong></p>
            </div>
            <div className={styles.referralActions}>
              <button
                className={styles.copyButton}
                onClick={handleCopyLink}
              >
                {copySuccess ? (
                  <>
                    <CheckCircleIcon />
                    Copied!
                  </>
                ) : (
                  <>
                    <ContentCopyIcon />
                    Copy Link
                  </>
                )}
              </button>
              <button
                className={styles.shareButton}
                onClick={handleShare}
              >
                <ShareIcon />
                Share
              </button>
            </div>
          </div>
        </div>

        {/* Send Invite Section */}
        <div className={styles.inviteSection}>
          <h3 className={styles.sectionTitle}>Send Personal Invitation</h3>
          
          <div className={styles.methodToggle}>
            <button
              className={`${styles.methodButton} ${inviteMethod === 'email' ? styles.methodActive : ''}`}
              onClick={() => setInviteMethod('email')}
            >
              <EmailIcon />
              Email
            </button>
            <button
              className={`${styles.methodButton} ${inviteMethod === 'sms' ? styles.methodActive : ''}`}
              onClick={() => setInviteMethod('sms')}
            >
              <SmsIcon />
              SMS
            </button>
          </div>

          <div className={styles.inviteForm}>
            <input
              type={inviteMethod === 'email' ? 'email' : 'tel'}
              className={styles.inviteInput}
              placeholder={inviteMethod === 'email' ? 'friend@email.com' : '+1 (555) 123-4567'}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleInvite()}
            />
            <button
              className={styles.sendButton}
              onClick={handleInvite}
              disabled={!recipient || isInviting}
            >
              {isInviting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Send Invite'
              )}
            </button>
          </div>

          {inviteError && (
            <p className={styles.errorMessage}>{inviteError}</p>
          )}

          {inviteSuccess && (
            <div className={styles.successMessage}>
              <CheckCircleIcon className={styles.successIcon} />
              Invitation sent successfully!
            </div>
          )}
        </div>

        {/* Invite History */}
        {inviteHistory.length > 0 && (
          <div className={styles.historySection}>
            <h3 className={styles.sectionTitle}>Invitation History</h3>
            <div className={styles.historyList}>
              {inviteHistory.map(invite => (
                <div key={invite.id} className={styles.historyItem}>
                  <div className={styles.historyInfo}>
                    <p className={styles.historyRecipient}>
                      {invite.recipientEmail || invite.recipientPhone}
                    </p>
                    <p className={styles.historyDate}>
                      Sent {formatDate(invite.sentAt)}
                      {invite.acceptedAt && ` • Joined ${formatDate(invite.acceptedAt)}`}
                    </p>
                  </div>
                  <div className={styles.historyStatus}>
                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(invite.status)}`}>
                      {invite.status}
                    </span>
                    {invite.rewardAmount && (
                      <span className={styles.rewardAmount}>+${invite.rewardAmount}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terms Section */}
        <div className={styles.termsSection}>
          <h4 className={styles.termsTitle}>How it works</h4>
          <ul className={styles.termsList}>
            <li>Send invitations to friends via email or SMS</li>
            <li>Friends must sign up using your referral link or code</li>
            <li>Both you and your friend receive $20 after their first appointment</li>
            <li>There's no limit to how many friends you can invite</li>
            <li>Rewards expire after 90 days if unused</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProfileInviteFriendsScreen;